/**
 * Das Anschreiben zum Belegversand — Platzhalter rein, Platzhalter raus.
 *
 * Gespeichert wird nie der fertige Text („Guten Tag Katharina Venus"), sondern
 * die Fassung mit Platzhaltern („Guten Tag {kunde}"). Sonst ginge die zweite
 * Mail mit dem Namen des ersten Kunden raus.
 *
 * Beide Richtungen stehen hier:
 *   • `vorlageFuellen`  — Vorlage → Text für diesen Beleg (Anzeige im Formular)
 *   • `alsVorlage`      — geschriebener Text → Vorlage (beim Merken)
 *
 * `vorlageFuellen` bildet `_fuellen` aus hetzner/webhook/kontor_mail.py nach.
 * Beide Seiten müssen dasselbe tun: der Server füllt, wenn ein Aufruf ohne Text
 * kommt, der Browser füllt für die Vorschau. Ändert sich eine Regel, ändern
 * sich beide.
 */
import { anredeFuer, type AnredeKunde } from "./anrede";
import { euro, istAngebotsbestaetigung, shortDate, type InvoiceRegisterItem } from "./invoices";

export const MAIL_PLATZHALTER = ["anrede", "kunde", "nummer", "betrag", "faellig", "art"] as const;

export type MailPlatzhalter = (typeof MAIL_PLATZHALTER)[number];
export type VersandWerte = Record<MailPlatzhalter, string>;

/**
 * Die Werte dieses Belegs, so wie sie in der Mail stehen sollen.
 *
 * `kunde` ist der Kundenstamm (Anrede, Name, Typ, Ansprechpartner), `empfaenger`
 * die erste Adresse im An-Feld — bei Firmen hängt die Anrede daran. Fehlt der
 * Kunde, rechnet sie wie der Server bei einem Beleg ohne Kunden.
 */
export function versandWerte(
  item: InvoiceRegisterItem, kundeName?: string, kunde?: AnredeKunde, empfaenger?: string,
): VersandWerte {
  const istAngebot = item.kind === "quote";
  const istBestaetigung = istAngebotsbestaetigung(item.kind, item.source_quote_nummer);
  const name = (kundeName || item.kunde_name || "").trim();
  return {
    anrede: anredeFuer({ ...kunde, name: kunde?.name || name }, empfaenger),
    kunde: name,
    nummer: item.nummer || "",
    betrag: item.brutto ? euro(item.brutto) : "",
    faellig: item.faellig_am ? shortDate(item.faellig_am) : "",
    art: istBestaetigung ? "Angebotsbestätigung" : istAngebot ? "Angebot" : "Rechnung",
  };
}

/**
 * Anschreiben zur Angebotsbestätigung (01.10.2026). Fest vorgegeben statt als
 * gemerkte Vorlage: Vorlagen liegen je Belegart, und die Bestätigung ist ein
 * Angebot — „Als Vorlage übernehmen" hätte das Angebots-Anschreiben überschrieben.
 * Im Formular bleibt der Text frei änderbar.
 */
export const BESTAETIGUNG_ANSCHREIBEN = {
  betreff: "Angebotsbestätigung {nummer} — Vimaana Garten",
  text: "{anrede},\n\n"
    + "vielen Dank für Ihren Auftrag. Anbei erhalten Sie unsere Angebotsbestätigung {nummer}.\n\n"
    + "Bitte bestätigen Sie uns den Auftrag: unterschrieben zurück oder mit einer kurzen Antwort auf diese Mail. "
    + "Erst dann ist er verbindlich erteilt.\n\n"
    + "Mit freundlichen Grüßen",
};

/**
 * Platzhalter durch die Werte des Belegs ersetzen.
 *
 * Ein leerer Wert nimmt das Leerzeichen davor mit — sonst stünde bei einem
 * Kunden ohne Namen „Guten Tag ," in der Mail.
 */
export function vorlageFuellen(text: string, werte: VersandWerte): string {
  let out = text || "";
  for (const name of MAIL_PLATZHALTER) {
    const wert = (werte[name] || "").trim();
    out = out.replace(
      new RegExp("([ \\u00a0]?)\\{" + name + "\\}", "g"),
      (_treffer, davor: string) => (wert ? davor + wert : ""),
    );
  }
  return out;
}

/**
 * Den geschriebenen Text zurück in eine Vorlage übersetzen.
 *
 * `art` bleibt bewusst aussen vor: Vorlagen liegen ohnehin je Belegart, und
 * aus „unser Angebot" würde „unser {art}" — was in der Rechnungsvorlage
 * „unser Rechnung" ergäbe. Werte unter drei Zeichen ebenfalls nicht, damit ein
 * kurzer Kundenname nicht mitten in anderen Wörtern zuschlägt.
 */
export function alsVorlage(text: string, werte: VersandWerte): string {
  let out = text || "";
  // Die Anrede nur am Anfang zurückübersetzen: „Guten Tag" oder „Hallo" kann
  // auch mitten im Text stehen und ist dort keine Anrede.
  const anrede = (werte.anrede || "").trim();
  if (anrede && out.startsWith(anrede)) out = `{anrede}${out.slice(anrede.length)}`;
  const paare = (["kunde", "nummer", "betrag", "faellig"] as MailPlatzhalter[])
    .map((name) => ({ name, wert: (werte[name] || "").trim() }))
    .filter((p) => p.wert.length >= 3)
    .sort((a, b) => b.wert.length - a.wert.length);
  for (const { name, wert } of paare) out = out.split(wert).join(`{${name}}`);
  return out;
}

/** Welche Platzhalter der gemerkte Text tatsächlich enthält — für den Hinweis. */
export function erkanntePlatzhalter(text: string): MailPlatzhalter[] {
  return MAIL_PLATZHALTER.filter((name) => (text || "").includes(`{${name}}`));
}
